import React, { useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Work,
  Assessment,
  Email,
  Security,
  Hub,
  IntegrationInstructions,
  TrendingUp,
  People,
  Groups,
  TrackChanges,
  Person,
  Analytics,
  Shield,
  Lock,
  Fingerprint,
  VerifiedUser,
  GppMaybe,
  Gavel,
  CheckCircle,
  AccessTime,
  AutoAwesome,
} from "@mui/icons-material";
import heroImage from "../Assests/Images/hero.png";
import overview from "../Assests/Images/overview.png";
import analytics from "../Assests/Images/analytics.png";
const MotionBox = motion.create(Box);
const MotionCard = motion.create(Card);

const DESIGN = {
  colors: {
    primaryDark: "#0A0F2C",
    secondaryIndigo: "#1E3A8A",
    primaryAccent: "#1E3A8A",
    accentLight: "#60A5FA",
    background: "#F8FAFC",
    cardBg: "#FFFFFF",
    textPrimary: "#0A0F2C", // Using primaryDark for better hierarchy
    textSecondary: "#475569",
    border: "rgba(226, 232, 240, 0.8)",
  },
  spacing: {
    container: { xs: '100%', lg: '1200px', xxl: '1440px', xxxl: '1800px' },
    sectionPadding: { xs: "32px 16px", md: "48px 0" },
    cardGap: 3, // 24px
    gridGap: 3, // 24px
    cardPadding: 4, // 32px
  },
  card: {
    borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    premiumShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    hover: {
      transform: "translateY(-4px)",
      boxShadow: "0 25px 50px -12px rgba(30, 58, 138, 0.25)",
    },
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  typography: {
    heroTitle: {
      fontSize: { xs: "42px", md: "56px" },
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    sectionTitle: {
      fontSize: { xs: "32px", md: "38px" },
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    body: {
      fontSize: "16px",
      lineHeight: 1.6,
    },
  },
};

function Landing() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* ================= HERO ================= */}
      <Box
        sx={{
          background: `radial-gradient(circle at 0% 0%, ${DESIGN.colors.secondaryIndigo} 0%, ${DESIGN.colors.primaryDark} 100%)`,
          color: "white",
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 5 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container, position: "relative", zIndex: 1, px: { xs: 2, md: 3 } }}>
          <Grid container spacing={DESIGN.spacing.gridGap} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ duration: 0.8 }}
              >
                <Typography sx={{ ...DESIGN.typography.heroTitle, mb: 1.5, fontSize: { xs: '32px', sm: '42px', md: '56px', xxl: '64px' } }}>
                  Empowering Connections <br />
                  <span style={{ color: DESIGN.colors.accentLight }}>Elevating Talents</span>
                </Typography>
                <Typography sx={{ ...DESIGN.typography.body, mb: 4, opacity: 0.9, maxWidth: "540px", color: "#CBD5E1", fontSize: { xs: '15px', md: '17px' } }}>
                  ExiHire helps modern organizations streamline the entire hiring
                  lifecycle from intelligent candidate sourcing and AI-powered
                  screening to structured interviews, analytics, collaboration,
                  and offer management. Built for startups, enterprises, and
                  growing teams, ExiHire enables faster hiring decisions, improved
                  recruiter productivity, and a smarter candidate experience
                  through automation and real-time insights.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/login")}
                    sx={{
                      px: 3.5,
                      py: 1.25,
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      bgcolor: DESIGN.colors.primaryAccent,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#1E40AF" },
                    }}
                  >
                    Start Your Journey
                  </Button>
                  {/*<Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate("/contact")}
                    sx={{
                      px: 3.5,
                      py: 1.25,
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      color: "white",
                      borderColor: "rgba(255,255,255,0.25)",
                      backdropFilter: "blur(10px)",
                      bgcolor: "rgba(255,255,255,0.05)",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "rgba(255,255,255,0.4)",
                      },
                    }}
                  >
                    Book a Demo
                  </Button> */}
                </Box>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "24px",
                    overflow: "hidden",
                    boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: "24px",
                      boxShadow: "0 0 40px rgba(30,58,138,0.18)",
                      pointerEvents: "none",
                    },
                    backgroundColor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={heroImage}
                    alt="ExiHire Platform"
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      borderRadius: "24px",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* ================= HERO STATS / QUICK FEATURES ================= */}
          <Grid
            container
            spacing={DESIGN.spacing.cardGap}
            sx={{
              mt: { xs: 4, md: 6 },
              pb: 2,
            }}
          >
            {[
              {
                icon: <People sx={{ color: DESIGN.colors.primaryAccent, fontSize: 28 }} />,
                title: "For Recruiters",
                desc: "Post jobs, screen candidates, hire faster",
              },
              {
                icon: <Person sx={{ color: DESIGN.colors.primaryAccent, fontSize: 28 }} />,
                title: "For Candidates",
                desc: "Find jobs, showcase skills, get hired",
              },
              {
                icon: <Security sx={{ color: DESIGN.colors.primaryAccent, fontSize: 28 }} />,
                title: "AI-Powered",
                desc: "Intelligent screening & assessment",
              },
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                    p: 3,
                    borderRadius: "24px",
                    bgcolor: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(12px)",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "rgba(30, 58, 138, 0.15)",
                      borderRadius: "16px",
                      width: 56,
                      height: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{ color: "white", mb: 0.5, fontSize: "1.1rem", fontWeight: 600 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#CBD5E1", lineHeight: 1.4 }}
                    >
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

        {/* ================= PLATFORM OVERVIEW ================= */}
        <Box
            id="platform-overview"
            sx={{
                pt: { xs: 4, md: 5 },
                pb: 0,
                bgcolor: DESIGN.colors.background,
                scrollMarginTop: '100px'
            }}
        >
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  p: 1,
                  background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
                  boxShadow: DESIGN.card.premiumShadow,
                }}
              >
                <Box
                  component="img"
                  src={overview}
                  alt="Platform Overview"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: "16px",
                    position: "relative",
                    zIndex: 1,
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  ...DESIGN.typography.sectionTitle,
                  color: DESIGN.colors.primaryDark,
                  mb: 2,
                }}
              >
                Platform Overview
              </Typography>

              <Typography sx={{ ...DESIGN.typography.body, color: DESIGN.colors.textSecondary, mb: 2 }}>
                ExiHire is an end-to-end recruitment platform centralizing every
                stage of your hiring journey. From posting jobs to onboarding, it
                integrates automation, analytics, and collaboration into one
                seamless system. Designed for startups, enterprises, and agencies,
                ExiHire simplifies complex workflows, improves decision accuracy,
                and enhances candidate experience.
              </Typography>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => navigate('/about')}
                  sx={{ 
                    borderRadius: "8px", 
                    px: 3, 
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    borderColor: DESIGN.colors.primaryAccent,
                    color: DESIGN.colors.primaryAccent,
                    "&:hover": {
                      borderColor: DESIGN.colors.secondaryIndigo,
                      bgcolor: "rgba(30,58,138,0.04)"
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================= WHY EXIHIRE ================= */}
      <Box id="why-exihire" sx={{ py: { xs: 4, md: 5 }, px: 0, bgcolor: DESIGN.colors.background, scrollMarginTop: '100px' }}>
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: "24px",
              bgcolor: "white",
              boxShadow: "0 10px 30px -5px rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(0, 0, 0, 0.02)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "28px", md: "36px" },
                fontWeight: 800,
                color: DESIGN.colors.primaryDark,
                mb: 1.5,
              }}
            >
              Why ExiHire?
            </Typography>

            {/* Accent Underline */}
            <Box
              sx={{
                width: "60px",
                height: "4px",
                background: "linear-gradient(90deg, #1E3A8A 0%, #9333EA 100%)",
                borderRadius: "2px",
                mb: 6,
              }}
            />

            <Grid container spacing={4} alignItems="flex-start">
              {/* Features List */}
              <Grid item xs={12} md={7}>
                <Grid container spacing={3}>
                  {[
                    { title: "AI Resume Screening", desc: "AI-powered resume evaluation." },
                    { title: "Automated Candidate Ranking", desc: "Intelligent talent prioritization." },
                    { title: "AI Coding Assessments", desc: "Automated technical skill assessment." },
                    { title: "Detailed Evaluation Reports", desc: "Comprehensive candidate insights." },
                    { title: "AI Technical Interviews", desc: "Structured technical evaluations." },
                    { title: "Skill Gap Analysis", desc: "Competency gap identification." },
                    { title: "AI HR Interviews", desc: "Behavioral and cultural assessment." },
                    { title: "Hiring Analytics Dashboard", desc: "Real-time hiring analytics." },
                  ].map((feature) => (
                    <Grid item xs={12} sm={6} key={feature.title}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(30, 58, 138, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CheckCircle sx={{ fontSize: 20, color: DESIGN.colors.primaryAccent }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: DESIGN.colors.primaryDark, fontSize: "17px", lineHeight: 1.3 }}>
                            {feature.title}
                          </Typography>
                          <Typography sx={{ color: DESIGN.colors.textSecondary, fontSize: "14px", mt: "4px", lineHeight: 1.4 }}>
                            {feature.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Stats / Benefits Area */}
              <Grid item xs={12} md={5} sx={{ position: "relative" }}>
                {/* Vertical Divider */}
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    left: 0,
                    top: "10%",
                    bottom: "10%",
                    width: "1.5px",
                    bgcolor: "#f1f5f9",
                  }}
                />
                <Box
                  sx={{
                    pl: { md: 5 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Grid container spacing={3}>
                    {[
                      { icon: TrendingUp, value: "95%", label: "Screening Time Reduction" },
                      { icon: AccessTime, value: "24/7", label: "Candidate Availability" },
                      { icon: AutoAwesome, value: "AI-Powered", label: "Hiring Platform" },
                      { icon: Security, value: "Enterprise-Grade", label: "Security & Compliance" },
                    ].map((item, idx) => (
                      <Grid item xs={6} sm={3} key={idx} sx={{ textAlign: "center" }}>
                        <Box
                          sx={{
                            width: { xs: 48, md: 60 },
                            height: { xs: 48, md: 60 },
                            bgcolor: "#f0f4ff",
                            borderRadius: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          <item.icon sx={{ fontSize: { xs: 24, md: 32 }, color: DESIGN.colors.primaryAccent }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, color: DESIGN.colors.primaryDark, mb: 0.5, fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                          {item.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: DESIGN.colors.textSecondary,
                            display: "block",
                            lineHeight: 1.3,
                            fontWeight: 600,
                            fontSize: "12px",
                            maxWidth: "100px",
                            mx: "auto",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Grid>
                    ))}

                  </Grid>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, pt: 4, borderTop: "1.5px solid #f1f5f9", textAlign: "center" }}>
              <Typography
                variant="body1"
                sx={{
                  color: DESIGN.colors.textSecondary,
                  maxWidth: "850px",
                  mx: "auto",
                  lineHeight: 1.6,
                  fontSize: "15px",
                  fontWeight: 500,
                }}
              >
                ExiHire is an AI-powered hiring platform that helps companies automate resume screening, coding assessments, technical interviews, HR interviews, candidate ranking, and hiring analytics.
                <br />
                Recruiters can identify top talent faster while reducing manual screening effort and improving hiring efficiency.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ================= SEAMLESS HIRING WORKFLOW ================= */}
      <Box id="how-it-works" sx={{ p: DESIGN.spacing.sectionPadding, bgcolor: DESIGN.colors.cardBg, scrollMarginTop: '100px' }}>
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Typography
            sx={{
              ...DESIGN.typography.sectionTitle,
              color: DESIGN.colors.primaryDark,
              textAlign: "center",
              mb: 2,
            }}
          >
            Seamless Hiring Workflow
          </Typography>
          <Typography
            sx={{ 
              ...DESIGN.typography.body,
              color: DESIGN.colors.textSecondary,
              textAlign: "center",
              mb: 4, 
              maxWidth: "700px", 
              mx: "auto" 
            }}
          >
            Streamline your recruitment process with our intuitive and powerful workflow management tools.
          </Typography>

          <Grid container spacing={DESIGN.spacing.cardGap}>
            {[
              {
                icon: <Work sx={{ color: DESIGN.colors.primaryAccent, fontSize: 32 }} />,
                title: "Post & Manage Jobs",
                desc: "Create detailed job listings, define interview stages, assign managers, track applicants, collaborate efficiently, and manage everything from one dashboard.",
              },
              {
                icon: <Assessment sx={{ color: DESIGN.colors.primaryAccent, fontSize: 32 }} />,
                title: "Assess Skills",
                desc: "Conduct automated skill evaluations, configure tests, enable scoring, reduce manual review, ensure objective comparison, and standardize hiring.",
              },
              {
                icon: <Email sx={{ color: DESIGN.colors.primaryAccent, fontSize: 32 }} />,
                title: "Interview & Offer",
                desc: "Schedule interviews, collect structured feedback, evaluate performance via analytics, generate offer letters instantly, and streamline approvals.",
              },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <MotionCard
                  whileHover={DESIGN.card.hover}
                  sx={{
                    p: DESIGN.spacing.cardPadding,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: DESIGN.card.borderRadius,
                    boxShadow: DESIGN.card.boxShadow,
                    border: `1px solid ${DESIGN.colors.border}`,
                    transition: DESIGN.card.transition,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "12px",
                      bgcolor: "rgba(30,58,138,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography sx={{ ...DESIGN.typography.cardTitle, color: DESIGN.colors.primaryDark, mb: 1.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ ...DESIGN.typography.body, color: DESIGN.colors.textSecondary, fontSize: "14.5px" }}>
                    {item.desc}
                  </Typography>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================= BUILT FOR EVERY HIRING TEAM ================= */}
      <Box id="built-for-teams" sx={{ p: DESIGN.spacing.sectionPadding, bgcolor: DESIGN.colors.background, scrollMarginTop: '100px' }}>
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Typography
            sx={{
              ...DESIGN.typography.sectionTitle,
              color: DESIGN.colors.primaryDark,
              textAlign: "center",
              mb: 3,
            }}
          >
            Built for Every Hiring Team
          </Typography>
          <Grid container spacing={DESIGN.spacing.cardGap}>
            {[
              {
                title: "Startups",
                desc: "Scale hiring with automation, collaborate efficiently, post jobs quickly, track pipelines, streamline interviews, and make fast decisions.",
              },
              {
                title: "Enterprises",
                desc: "Secure and compliant recruitment at scale. Standardize processes, track analytics, and streamline approvals across teams.",
              },
              {
                title: "Agencies",
                desc: "Manage multiple clients easily, automate assessments, communicate seamlessly, monitor requirements, and ensure top talent delivery.",
              },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <MotionCard
                  whileHover={DESIGN.card.hover}
                  sx={{
                    p: DESIGN.spacing.cardPadding,
                    height: "100%",
                    bgcolor: DESIGN.colors.cardBg,
                    borderRadius: DESIGN.card.borderRadius,
                    boxShadow: DESIGN.card.boxShadow,
                    border: `1px solid ${DESIGN.colors.border}`,
                    transition: DESIGN.card.transition,
                  }}
                >
                  <Typography
                    sx={{ color: DESIGN.colors.primaryAccent, mb: 1.5, fontWeight: 800, fontSize: "20px" }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ ...DESIGN.typography.body, color: DESIGN.colors.textPrimary, fontSize: "15px" }}>
                    {item.desc}
                  </Typography>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

        {/* ================= POWERFUL HIRING ANALYTICS ================= */}
        <Box
            id="hiring-analytics"
            sx={{
                p: DESIGN.spacing.sectionPadding,
                bgcolor: DESIGN.colors.cardBg,
                scrollMarginTop: '100px'
            }}
        >
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Grid container spacing={6} alignItems="center">
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  ...DESIGN.typography.sectionTitle,
                  color: DESIGN.colors.primaryDark,
                  mb: 3,
                }}
              >
                Powerful Hiring Analytics
              </Typography>

              <Typography sx={{ ...DESIGN.typography.body, color: DESIGN.colors.textSecondary, mb: 5 }}>
                Track candidate performance, time-to-hire, and interview success
                with real-time insights. Visualize recruitment metrics, make
                data-driven decisions, and optimize your hiring workflow
                effectively.
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    icon: <Analytics sx={{ color: "white", fontSize: 24 }} />,
                    title: "Candidate Reports",
                    desc: "Detailed insights on candidate pipeline and performance.",
                  },
                  {
                    icon: <TrendingUp sx={{ color: "white", fontSize: 24 }} />,
                    title: "Hiring Trends",
                    desc: "Track hiring patterns and identify top-performing sources.",
                  },
                  {
                    icon: <People sx={{ color: "white", fontSize: 24 }} />,
                    title: "Performance",
                    desc: "Measure team efficiency and improve hiring outcomes.",
                  },
                ].map((item, i) => (
                  <Grid item xs={12} sm={4} key={i}>
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: DESIGN.colors.primaryAccent,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                          mb: 2,
                          boxShadow: "0 8px 16px -4px rgba(30, 58, 138, 0.4)",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{ color: DESIGN.colors.primaryDark, mb: 1, fontSize: "0.95rem", fontWeight: 700 }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: DESIGN.colors.textSecondary, lineHeight: 1.4, fontSize: "0.85rem" }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  p: 1,
                  background: "linear-gradient(145deg, #ffffff, #f8fafc)",
                  boxShadow: DESIGN.card.premiumShadow,
                }}
              >
                <Box
                  component="img"
                  src={analytics}
                  alt="Hiring Analytics"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: "16px",
                    position: "relative",
                    zIndex: 1,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================= ENTERPRISE-GRADE SECURITY ================= */}
      <Box id="security" sx={{ p: DESIGN.spacing.sectionPadding, bgcolor: DESIGN.colors.background, scrollMarginTop: '100px' }}>
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Typography
            sx={{
              ...DESIGN.typography.sectionTitle,
              color: DESIGN.colors.primaryDark,
              textAlign: "center",
              mb: 3,
            }}
          >
            Enterprise-Grade Security
          </Typography>
          <Grid container spacing={DESIGN.spacing.cardGap}>
            {[
              {
                icon: <Security />,
                title: "Data Encryption",
                desc: "AES-256 encryption for all candidate data.",
              },
              {
                icon: <Hub />,
                title: "Access Control",
                desc: "Role-based access for teams and admins.",
              },

              {
                icon: <Shield />,
                title: "Secure Infrastructure",
                desc: "Cloud-based secure architecture.",
              },
              {
                icon: <Lock />,
                title: "Two-Factor Authentication",
                desc: "Enhanced login security for all users.",
              },
              {
                icon: <VerifiedUser />,
                title: "Audit Logs",
                desc: "Track all actions in the system for accountability.",
              },
              {
                icon: <GppMaybe />,
                title: "Vulnerability Monitoring",
                desc: "Continuous monitoring and protection against security threats.",
              },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  sx={{
                    p: 3.5,
                    height: "100%",
                    bgcolor: DESIGN.colors.cardBg,
                    borderRadius: DESIGN.card.borderRadius,
                    border: `1px solid ${DESIGN.colors.border}`,
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    transition: DESIGN.card.transition,
                    "&:hover": {
                      borderColor: DESIGN.colors.primaryAccent,
                      boxShadow: DESIGN.card.hover.boxShadow,
                      transform: DESIGN.card.hover.transform,
                    }
                  }}
                >
                  <Box
                    sx={{
                      color: DESIGN.colors.primaryAccent,
                      bgcolor: "rgba(30,58,138,0.08)",
                      borderRadius: "10px",
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(item.icon, { fontSize: "small" })}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        color: DESIGN.colors.primaryDark,
                        fontWeight: 700,
                        fontSize: "1rem",
                        mb: 0.75,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: DESIGN.colors.textSecondary, lineHeight: 1.5, fontSize: "14px" }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================= OUR GOALS ================= */}
      <Box sx={{ p: DESIGN.spacing.sectionPadding, bgcolor: DESIGN.colors.cardBg }}>
        <Container maxWidth={false} sx={{ maxWidth: DESIGN.spacing.container }}>
          <Typography
            sx={{
              ...DESIGN.typography.sectionTitle,
              color: DESIGN.colors.primaryDark,
              textAlign: "center",
              mb: 3,
            }}
          >
            Our Philosophy
          </Typography>

          <Grid container spacing={DESIGN.spacing.cardGap}>
            {[
              {
                title: "Simplify Hiring for Everyone",
                desc: "We aim to make recruitment effortless, efficient, and accessible for businesses of all sizes with intelligent automation at every step.",
                icon: <TrackChanges />,
                color: "#EEF2FF",
                iconColor: "#1E3A8A",
              },
              {
                title: "Deliver Data-Driven Decisions",
                desc: "We empower recruiters with real-time insights and analytics to make smarter hiring decisions and build high-performing teams.",
                icon: <TrendingUp />,
                color: "#ECFDF5",
                iconColor: "#10B981",
              },
              {
                title: "Enhance Candidate Experience",
                desc: "We focus on creating a seamless, transparent, and engaging journey for candidates from application to onboarding.",
                icon: <Groups />,
                color: "#EFF6FF",
                iconColor: "#3B82F6",
              },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <MotionCard
                  whileHover={DESIGN.card.hover}
                  sx={{
                    p: DESIGN.spacing.cardPadding,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: DESIGN.card.borderRadius,
                    boxShadow: DESIGN.card.boxShadow,
                    border: `1px solid ${DESIGN.colors.border}`,
                    transition: DESIGN.card.transition,
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "12px",
                      bgcolor: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { color: item.iconColor, fontSize: 28 } })}
                  </Box>

                  <Typography
                    sx={{
                      ...DESIGN.typography.cardTitle,
                      color: DESIGN.colors.primaryDark,
                      mb: 2,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Box sx={{ width: "100%", height: "1px", bgcolor: DESIGN.colors.border, mb: 3 }} />

                  <Typography
                    sx={{
                      ...DESIGN.typography.body,
                      color: DESIGN.colors.textSecondary,
                      fontSize: "15px",
                    }}
                  >
                    {item.desc}
                  </Typography>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Landing;
